<script setup lang="ts">
import { ref, onBeforeMount, inject, computed, onMounted, h, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { AIR_ONLY, CRUISE, HOTEL_ONLY, PACKAGE, BOOKING_FORM, TOUR, LANG_FRENCH, INCENTIVES } from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useBookingQuoteStore } from '@/store/BookingQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useTermsAndConditionsStore } from '@/store/TermsAndConditionsStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useViewComposable } from '@/views/composable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import Button from 'primevue/button';
import AirOption from './book/AirOption.vue';
import ConvertQuoteBookingModal from '@/components/modal/ConvertQuoteBookingModal.vue';
import CruiseOption from './book/CruiseOption.vue';
import HotelOption from './book/HotelOption.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PackageOption from './book/PackageOption.vue';
import PriceBookingSummaryModal from '@/components/modal/priceBooking/PriceBookingSummaryModal.vue';
import TourOption from '@/components/book/TourOption.vue';

const dialogRef = inject('dialogRef') as Ref;
const isManagement = dialogRef?.value?.data?.isManagement;
const saveBooking = dialogRef?.value?.options?.emits?.saveBooking;

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const toast = useToast();
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const termsAndConditionsStore = useTermsAndConditionsStore();
const adminSetupStore = useAdminSetupStore();
const bookingQuoteStore = useBookingQuoteStore();
const viewQuoteStore = useViewQuoteStore();
const { priorityStatus, statusPriorityNumber } = useViewComposable();
const products = ref();
const termAndCondition = ref(null);
const activePanel = ref();
const isLoading = ref(false);

const quote = computed(() => bookingQuoteStore.quote);

const getBookingTermsAndConditions = async () => {
  const qType = quote.value.gType.includes('Incentive') ? INCENTIVES : quote.value.qType;
  const response = await termsAndConditionsStore.getData(BOOKING_FORM, qType);
  termAndCondition.value = response;
};

const renderIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-intro', innerHTML: termAndCondition.value.introFr });
  } else {
    return h('div', { class: 'english-intro', innerHTML: termAndCondition.value.intro });
  }
};

const renderTermsAndConditionsHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-term', innerHTML: termAndCondition.value.termsFr });
  } else {
    return h('div', { class: 'english-term', innerHTML: termAndCondition.value.terms });
  }
};

const initProducts = () => {
  switch (quote.value.qType) {
    case PACKAGE:
      products.value = quote.value.packages;
      break;
    case HOTEL_ONLY:
      products.value = quote.value.hotels;
      break;
    case AIR_ONLY:
      products.value = quote.value.airs;
      break;
    case CRUISE:
      products.value = quote.value.cruises;
      break;
    default:
      break;
  }
};

const onConvert = () => {
  const dialogRef = dialog.open(ConvertQuoteBookingModal, {
    props: {
      header: t('booking.header'),
      modal: true
    },
    data: {
      quoteId: quote.value.sk,
      bookingData: {
        bookingId: quote.value?.bookingId,
        depositDate: quote.value?.depositDate,
        finalPaymentDate: quote.value?.finalPaymentDate,
        depositAmount: quote.value?.depositAmount,
        finalPayment: quote.value?.finalPayment,
        passengerNameDueDate: quote.value?.passengerNameDueDate
      }
    },
    emits: {
      isSave: (value) => {
        handleSave(value, dialogRef);
      }
    }
  });
};

const handleSave = (value: any, dialogRef: any) => {
  if (value === true) {
    dialogRef.close();
    saveBooking(true);
  } else {
    dialogRef.close();
    saveBooking(false);
  }
};

const renderHeader = () => {
  const bookingId = quote.value.bookingId?.split(',')?.join(', ');
  const prefix = bookingId ? `| ${bookingId}` : '';
  return `${quote.value.sk} ${prefix} - ${t('common.buttons.booking_price_summary')}`;
};

const handleViewBookingSummary = () => {
  isLoading.value = true;
  viewQuoteStore
    .viewPriceSummary(quote.value.sk)
    .then((data: any) => {
      openPriceBookingSummary(data);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const openPriceBookingSummary = (priceSummary: any) => {
  dialog.open(PriceBookingSummaryModal, {
    props: {
      header: renderHeader(),
      style: { width: '90vw' },
      modal: true,
      maximizable: true
    },
    data: {
      quoteType: quote.value?.qType,
      priceSummary
    }
  });
};

onBeforeMount(() => {
  initProducts();
});

onMounted(() => {
  adminSetupStore.getMinimumConfig();
  getBookingTermsAndConditions();
});
</script>

<template>
  <div class="book-form">
    <LoadingSpinner v-if="isLoading" />
    <h2>{{ $t('booking.rooming_title') }} {{ quote.sk }}</h2>
    <div class="book-title">
      <renderIntroHtml v-if="termAndCondition"></renderIntroHtml>
    </div>
    <hr />
    <div class="mb-2 flex gap-2">
      <div class="grid" v-if="authorizations?.quote_management?.tts_booking_view || authorizations?.quote_management?.tts_booking_edit">
        <div class="col">
          <Button v-if="priorityStatus.get(quote.quoteSts) >= statusPriorityNumber.FOUR" class="bg-primary mr-3" :label="$t('my_quote.convert_quote')" @click="onConvert()" />
        </div>
      </div>
      <div class="grid">
        <div class="col">
          <Button v-if="priorityStatus.get(quote.quoteSts) >= statusPriorityNumber.FOUR" class="bg-primary mr-3 booking-price-summary-btn" :label="$t('common.buttons.booking_price_summary')" @click="handleViewBookingSummary()" />
        </div>
      </div>
    </div>
    <div class="grid">
      <div class="col-12 bg-blue-5">
        <div class="card">
          <div v-if="quote.qType === PACKAGE"><PackageOption :isManagement="isManagement" /></div>
          <div v-if="quote.qType === HOTEL_ONLY"><HotelOption :isManagement="isManagement" /></div>
          <div v-if="quote.qType === AIR_ONLY"><AirOption :isManagement="isManagement" /></div>
          <div v-if="quote.qType === CRUISE"><CruiseOption :isManagement="isManagement" /></div>
          <div v-if="quote.qType === TOUR"><TourOption :isManagement="isManagement" /></div>
        </div>
      </div>
    </div>
    <Accordion v-model:value="activePanel" class="acvShadow">
      <AccordionPanel value="0">
        <AccordionHeader>
          <div class="h6style m-0">{{ $t('booking.terms_and_conditions') }}</div>
          <div class="absolute px-3 flex align-items-center bg-red-500 expand-icon">
            <i v-if="activePanel != 0" class="pi pi-plus-circle text-4xl text-white-alpha-90"></i>
            <i v-else class="pi pi-minus-circle text-4xl text-white-alpha-90"></i>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <div class="book-term">
            <renderTermsAndConditionsHtml v-if="termAndCondition"></renderTermsAndConditionsHtml>
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>
